import { Check, Pencil, StickyNote, X } from "lucide-react";
import { Input } from "../../../../components/ui/common/input";
import { cn } from "../../../../lib/utils";
import { RecipeVariantColumn } from "./recipeVariantTable.types";

interface RecipeVariantColumnHeaderProps {
  column: RecipeVariantColumn;
  readOnly: boolean;
  isEditing: boolean;
  editNameValue: string;
  isSaving: boolean;
  variantNameMaxLength: number;
  onEditNameValueChange: (value: string) => void;
  onStartEdit: () => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onOpenEditVariation: () => void;
}

const RecipeVariantColumnHeader = ({
  column,
  readOnly,
  isEditing,
  editNameValue,
  isSaving,
  variantNameMaxLength,
  onEditNameValueChange,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onOpenEditVariation
}: RecipeVariantColumnHeaderProps) => {
  const hasUpdates = column.hasUpdatedIngredients;
  const hasNotes = Boolean(column.notes?.trim());
  const canEdit = !readOnly && Boolean(column.variantId);

  return (
    <th className="tw-min-w-[200px] tw-bg-primary tw-px-4 tw-py-3 tw-text-center tw-font-semibold tw-text-primary-foreground">
      <div className="tw-flex tw-items-center tw-justify-center tw-gap-2">
        {isEditing ? (
          <div className="tw-flex tw-items-center tw-gap-1">
            <Input
              value={editNameValue}
              onChange={event => onEditNameValueChange(event.target.value)}
              className="tw-h-6 tw-w-28 tw-bg-primary-foreground tw-text-xs tw-text-foreground"
              maxLength={variantNameMaxLength}
              autoFocus
              disabled={isSaving}
              onKeyDown={event => {
                if (event.key === "Enter") {
                  onSaveEdit();
                }

                if (event.key === "Escape") {
                  onCancelEdit();
                }
              }}
            />
            <button
              type="button"
              onClick={onSaveEdit}
              disabled={isSaving || !editNameValue.trim()}
              className="tw-text-primary-foreground hover:tw-opacity-80 disabled:tw-opacity-50"
              aria-label="Save variation name"
            >
              <Check className="tw-h-3.5 tw-w-3.5" />
            </button>
            <button
              type="button"
              onClick={onCancelEdit}
              disabled={isSaving}
              className="tw-text-primary-foreground hover:tw-opacity-80 disabled:tw-opacity-50"
              aria-label="Cancel editing variation name"
            >
              <X className="tw-h-3.5 tw-w-3.5" />
            </button>
          </div>
        ) : (
          <>
            {canEdit ? (
              <button
                type="button"
                onClick={onOpenEditVariation}
                className={cn(
                  "tw-line-clamp-2 tw-cursor-pointer hover:tw-underline",
                  hasUpdates ? "tw-text-[#F2A86F]" : "tw-text-white"
                )}
                aria-label={`Edit ${column.title}`}
              >
                {column.title}
              </button>
            ) : (
              <span
                className={cn(
                  "tw-line-clamp-2",
                  hasUpdates ? "tw-text-[#F2A86F]" : "tw-text-white"
                )}
              >
                {column.title}
              </span>
            )}
            {canEdit && (
              <>
                <button
                  type="button"
                  onClick={onStartEdit}
                  className="tw-text-primary-foreground hover:tw-opacity-80"
                  aria-label={`Edit ${column.title} name`}
                >
                  <Pencil className="tw-h-3 tw-w-3" />
                </button>
                <button
                  type="button"
                  onClick={onOpenEditVariation}
                  className="tw-text-primary-foreground hover:tw-opacity-80"
                  title={hasNotes ? "Edit notes" : "Add notes"}
                  aria-label={hasNotes ? "Edit notes" : "Add notes"}
                >
                  <StickyNote
                    className={cn("tw-h-3.5 tw-w-3.5", !hasNotes && "tw-opacity-70")}
                  />
                </button>
              </>
            )}
          </>
        )}
      </div>
    </th>
  );
};

export default RecipeVariantColumnHeader;
